import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { DragRunArtifact, DragRunSeries } from 'src/app/model/response/ai-chat-response';
import {
  buildTimeline, clamp, distanceAt, gearAt, indexAt, laneXAt, laneXAtDistance, timeAtLaneX,
  twoStepArmStart, LaneGeometry, Timeline,
} from './drag-run-replay';
import { formatTruncated } from 'src/app/shared/number-format.util';

/** Um ponto plotável. Segmentos separados viram linhas separadas no gráfico. */
export interface DragRunPoint { t: number; v: number; }

export interface DragRunSlipCell {
  label: string;
  value: string;
  meta: string;
  unavailable: boolean;
  /** Instante em que a célula "acontece" na corrida; até lá o replay a mostra pendente. */
  t?: number;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** Geometria da faixa, em unidades do viewBox. */
const LANE: LaneGeometry = { w: 800, h: 72, x0: 74, x1: 776, y: 40 };

/** Área de desenho de cada medidor, em unidades do viewBox. */
const GAUGE_W = 200;
const GAUGE_H = 44;

/** Passo das setas do teclado na faixa. */
const KEY_STEP_S = 0.1;
/** Quanto tempo o carro anuncia a marcha nova depois de uma troca. */
const SHIFT_HOLD_S = 0.9;
/** Quanto tempo a linha de largada fica verde depois da soltura. */
const GO_HOLD_S = 1.2;

const REASON_KEYS: Record<string, string> = {
  no_launch_in_log: 'chatbot.dragRun.reasonNoLaunchInLog',
  channels_missing: 'chatbot.dragRun.reasonChannelsMissing',
  not_reached: 'chatbot.dragRun.notReached',
};

interface GaugeSpec {
  key: keyof DragRunSeries;
  labelKey: string;
  unit: string;
  color: string;
  digits: number;
  signed?: boolean;
}

/** Canais dos medidores, na ordem da Seção 9. A unidade já vem no nome do campo. */
const GAUGES: GaugeSpec[] = [
  { key: 'rpm',       labelKey: 'chatbot.dragRun.channelRpm',     unit: 'rpm',  color: '#00FF00', digits: 0 },
  { key: 'map_bar',   labelKey: 'chatbot.dragRun.channelBoost',   unit: 'bar',  color: '#FF8A00', digits: 2, signed: true },
  { key: 'oil_bar',   labelKey: 'chatbot.dragRun.channelOil',     unit: 'bar',  color: '#9B51E0', digits: 2 },
  { key: 'fuel_bar',  labelKey: 'chatbot.dragRun.channelFuel',    unit: 'bar',  color: '#F2C94C', digits: 2 },
  { key: 'kmh',       labelKey: 'chatbot.dragRun.channelSpeed',   unit: 'km/h', color: '#FFFFFF', digits: 0 },
  { key: 'battery_v', labelKey: 'chatbot.dragRun.channelBattery', unit: 'V',    color: '#7DBFFF', digits: 2 },
];

// Valores exibidos são cortados (nunca arredondados) e limitados a 3 casas.
const num = (v: number, d = 0) =>
  formatTruncated(v, { locale: 'pt-BR', minDecimals: d, maxDecimals: d });
const fixedPtBr = (v: number, d: number) =>
  formatTruncated(v, { locale: 'pt-BR', minDecimals: d, maxDecimals: d, useGrouping: false });
// O sinal sai do texto cortado: delta negativo que trunca para zero não vira '−0,00'.
export const signed = (v: number, d = 2) => {
  const t = fixedPtBr(Math.abs(v), d);
  return (v < 0 && /[1-9]/.test(t) ? '−' : '+') + t;
};
const secs = (v: number, d = 2) => fixedPtBr(v, d);
export const signedSecs = (v: number) => {
  const t = fixedPtBr(Math.abs(v), 2);
  return (v < 0 && /[1-9]/.test(t) ? '−' : '') + t;
};

/**
 * Quebra um canal em trechos contínuos. `null` — no valor OU no tempo da amostra — encerra o
 * trecho corrente, porque é lacuna de gravação: ligar os dois lados desenharia um dado que
 * não existe, e interpolar sobre a lacuna é pior ainda num gráfico de corrida.
 */
export function dragRunSegments(
  times: (number | null)[],
  values: (number | null)[],
): DragRunPoint[][] {
  const segments: DragRunPoint[][] = [];
  let current: DragRunPoint[] = [];

  for (let i = 0; i < values.length; i++) {
    const t = times[i];
    const v = values[i];
    if (t === null || t === undefined || v === null || v === undefined) {
      if (current.length) { segments.push(current); current = []; }
      continue;
    }
    current.push({ t, v });
  }
  if (current.length) segments.push(current);
  return segments;
}

/**
 * Monta as quatro células do topo: reação e as três parciais, nesta ordem.
 *
 * Nenhuma célula some quando o dado falta (Seção 7): ela passa a exibir o motivo. Essa é a
 * regra central do card — um tempo ausente renderizado como "0,00 s" afirmaria uma largada
 * instantânea, que é o oposto do que aconteceu.
 */
export function dragRunSlipCells(data: DragRunArtifact, t: Translate): DragRunSlipCell[] {
  const lifted = data.flags.includes('lift_before_finish');

  const reaction: DragRunSlipCell = data.reaction.available
    ? {
        label: t('chatbot.dragRun.reaction'),
        value: secs(data.reaction.s ?? 0, 3),
        meta: t('chatbot.dragRun.reactionMeta'),
        unavailable: false,
        t: data.reaction.s,
      }
    : {
        label: t('chatbot.dragRun.reaction'),
        value: t(REASON_KEYS[data.reaction.reason ?? ''] ?? 'chatbot.dragRun.unavailable'),
        meta: '',
        unavailable: true,
      };

  const splits = data.splits.map((s) => {
    if (!s.available) {
      return {
        label: s.label,
        value: t('chatbot.dragRun.notReached'),
        meta: lifted && s.id === '1/8' ? t('chatbot.dragRun.liftedBefore') : '',
        unavailable: true,
      };
    }
    const bits = [`${num(s.kmh ?? 0)} km/h`, `${s.gear ?? '—'}ª`];
    if (s.uncertainty_s !== undefined) bits.push(`±${secs(s.uncertainty_s)}`);
    return {
      label: s.label,
      value: secs(s.t_s ?? 0),
      meta: bits.join(' · '),
      unavailable: false,
      t: s.t_s,
    };
  });

  return [reaction, ...splits];
}

/* ───────────── modelos de tela: o que é fixo por corrida e o que muda a cada quadro ───────────── */

interface LaneView {
  phases: { x: number; w: number; color: string; title: string }[];
  marks: { id: string; x: number; label: string; anchor: string }[];
  events: { x: number; kind: string; title: string }[];
}

interface GaugeView extends GaugeSpec {
  label: string;
  available: boolean;
  paths: string[];
  areas: string[];
  peak: string;
  t0x: number;
  xOf: (t: number) => number;
  yOf: (v: number) => number;
}

interface FrameGauge {
  value: string;
  cx: number;
  cy: number | null;
}

/** Tudo que depende do instante corrente. Recalculado a cada quadro, então só o essencial. */
interface Frame {
  t: number;
  atEnd: boolean;
  carX: number;
  carGear: string;
  armed: boolean;
  go: boolean;
  stageLabel: string;
  hit: Set<string>;
  gauges: FrameGauge[];
  timeStrong: string;
  timeRest: string;
  ariaNow: number;
}

@Component({
  selector: 'app-drag-run-card',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="drag-run" [attr.aria-label]="t('chatbot.dragRun.aria')">
      <header class="dr-head">
        <i class="pi pi-flag"></i>
        <span class="name">{{ data.file_name }}</span>
        <span class="vchip" [ngClass]="data.run.validity">{{ validityLabel }}</span>
        <span class="meta">{{ headerMeta }}</span>
      </header>

      <div class="dr-slip">
        <div class="cell" *ngFor="let c of slipCells">
          <div class="eyebrow">{{ c.label }}</div>
          <div class="v" [class.na]="c.unavailable" [class.pending]="isPending(c)">
            {{ c.value }}<small *ngIf="!c.unavailable">s</small>
          </div>
          <div class="m" [class.lift]="c.unavailable && c.meta">{{ c.meta }}</div>
        </div>
      </div>

      <div class="dr-hl" *ngIf="data.highlights.length">
        <span class="hl" *ngFor="let h of data.highlights" [ngClass]="h.level">{{ h.text }}</span>
      </div>

      <div class="dr-track">
        <button type="button" class="dr-play" (click)="togglePlay()"
                [attr.aria-label]="playLabel" [attr.aria-pressed]="playing">
          <i class="pi" [ngClass]="playing ? 'pi-pause' : 'pi-play'"></i>
        </button>

        <svg #laneEl class="dr-lane" [attr.viewBox]="'0 0 ' + LANE.w + ' ' + LANE.h"
             preserveAspectRatio="none" role="slider" tabindex="0"
             [attr.aria-label]="t('chatbot.dragRun.timeAria')"
             aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="frame.ariaNow"
             [attr.aria-valuetext]="frame.timeStrong + ' ' + frame.timeRest"
             (pointerdown)="onPointerDown($event, laneEl)"
             (pointermove)="onPointerMove($event, laneEl)"
             (pointerup)="onPointerUp()" (pointercancel)="onPointerUp()"
             (keydown)="onKeydown($event)">
          <defs>
            <pattern id="drChk" width="4" height="4" patternUnits="userSpaceOnUse">
              <rect width="2" height="2" fill="#d5d5d5"></rect>
              <rect x="2" y="2" width="2" height="2" fill="#d5d5d5"></rect>
            </pattern>
          </defs>

          <rect *ngFor="let p of lane.phases" class="ph-band"
                [attr.x]="p.x" [attr.y]="LANE.y - 24" [attr.width]="p.w" height="7"
                [attr.fill]="p.color">
            <title>{{ p.title }}</title>
          </rect>

          <rect class="pre-bg" x="0" [attr.y]="LANE.y - 12" [attr.width]="LANE.x0" height="24"></rect>
          <rect class="lane-bg" [attr.x]="LANE.x0" [attr.y]="LANE.y - 12"
                [attr.width]="LANE.x1 - LANE.x0" height="24"></rect>
          <line class="lane-edge" [attr.x1]="LANE.x0" [attr.y1]="LANE.y - 12"
                [attr.x2]="LANE.x1" [attr.y2]="LANE.y - 12"></line>
          <line class="lane-edge" [attr.x1]="LANE.x0" [attr.y1]="LANE.y + 12"
                [attr.x2]="LANE.x1" [attr.y2]="LANE.y + 12"></line>

          <text class="stage-t" [class.armed]="frame.armed"
                [attr.x]="LANE.x0 / 2" [attr.y]="LANE.y + 26" text-anchor="middle">{{ frame.stageLabel }}</text>

          <line *ngFor="let m of lane.marks" class="mk" [class.hit]="frame.hit.has(m.id)"
                [attr.x1]="m.x" [attr.y1]="LANE.y - 12" [attr.x2]="m.x" [attr.y2]="LANE.y + 12"></line>
          <text *ngFor="let m of lane.marks" class="mk-t" [class.hit]="frame.hit.has(m.id)"
                [attr.x]="m.x" [attr.y]="LANE.y + 26" [attr.text-anchor]="m.anchor">{{ m.label }}</text>

          <line *ngFor="let e of lane.events" class="evt" [ngClass]="e.kind"
                [attr.x1]="e.x" [attr.y1]="LANE.y - 12" [attr.x2]="e.x" [attr.y2]="LANE.y - 17">
            <title>{{ e.title }}</title>
          </line>

          <rect [attr.x]="LANE.x1 - 3" [attr.y]="LANE.y - 12" width="6" height="24"
                fill="url(#drChk)"></rect>
          <line class="start" [class.armed]="frame.armed" [class.go]="frame.go"
                [attr.x1]="LANE.x0" [attr.y1]="LANE.y - 14"
                [attr.x2]="LANE.x0" [attr.y2]="LANE.y + 14"></line>

          <g [attr.transform]="'translate(' + frame.carX + ',' + LANE.y + ')'">
            <rect class="car" x="-11" y="-5" width="22" height="10" rx="2"></rect>
            <rect class="car" x="-13" y="-6.5" width="2.5" height="13"></rect>
            <text class="car-g" x="0" y="-9" text-anchor="middle">{{ frame.carGear }}</text>
          </g>
        </svg>

        <div class="dr-time" aria-hidden="true">
          <b>{{ frame.timeStrong }}</b><span *ngIf="frame.timeRest"> · {{ frame.timeRest }}</span>
        </div>
      </div>

      <div class="dr-phases" *ngIf="phaseLegend.length">
        <span *ngFor="let p of phaseLegend">
          <i [style.background]="p.color"></i>{{ p.label }}
        </span>
      </div>

      <div class="dr-ctx">
        <ng-container *ngFor="let row of contextRows">
          <span class="eyebrow">{{ row.label }}</span>
          <span class="vals" [class.na]="row.unavailable">{{ row.value }}</span>
        </ng-container>
      </div>

      <div class="dr-gauges">
        <div class="gauge" *ngFor="let g of gauges; let i = index" [class.off]="!g.available"
             [style.--c]="g.color">
          <div class="top">
            <span class="sw"></span>
            <span class="lb">{{ g.label }}</span>
            <span class="peak" *ngIf="g.available">{{ g.peak }}</span>
          </div>
          <div class="row">
            <span class="cv">{{ g.available ? frame.gauges[i].value : t('chatbot.dragRun.noChannel') }}</span>
            <span class="u" *ngIf="g.available">{{ g.unit }}</span>
          </div>
          <svg *ngIf="g.available" [attr.viewBox]="'0 0 ' + GAUGE_W + ' ' + GAUGE_H"
               preserveAspectRatio="none" aria-hidden="true">
            <path *ngFor="let d of g.areas" class="ar" [attr.d]="d"></path>
            <path *ngFor="let d of g.paths" class="ln" [attr.d]="d"></path>
            <line class="t0" [attr.x1]="g.t0x" y1="0" [attr.x2]="g.t0x" [attr.y2]="GAUGE_H"></line>
            <line class="cur" [attr.x1]="frame.gauges[i].cx" y1="0"
                  [attr.x2]="frame.gauges[i].cx" [attr.y2]="GAUGE_H"></line>
            <circle *ngIf="frame.gauges[i].cy !== null" class="pt" r="3"
                    [attr.cx]="frame.gauges[i].cx" [attr.cy]="frame.gauges[i].cy"></circle>
          </svg>
        </div>
      </div>

      <footer class="dr-foot">{{ footerNote }}</footer>
    </section>
  `,
  styles: [`
    .drag-run { background: #0b0c0e; border: 1px solid #303030; margin-top: 8px; overflow: hidden; }

    .dr-head { display: flex; align-items: center; gap: 8px; padding: 8px 12px;
               border-bottom: 1px solid #303030; color: #d5d5d5; font-size: 12px; }
    .dr-head .pi { font-size: 13px; color: #9b9b9b; }
    .dr-head .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .dr-head .meta { color: #9b9b9b; margin-left: auto; white-space: nowrap; }
    .vchip { flex: 0 0 auto; font-size: 10px; font-weight: 600; letter-spacing: .07em;
             text-transform: uppercase; padding: 2px 7px; border: 1px solid currentColor; }
    .vchip.valid { color: var(--ftds-success, #22c55e); }
    .vchip.unknown { color: var(--ftds-text-dim, #888); }
    .vchip.red_light, .vchip.aborted_mechanical { color: var(--ftds-danger, #ef4444); }
    .vchip.test_only { color: var(--ftds-warn, #f0b429); }

    .dr-slip { display: grid; grid-template-columns: repeat(4, 1fr); padding: 12px 12px 10px; gap: 8px; }
    .dr-slip .cell { min-width: 0; padding-left: 10px; border-left: 1px solid #252525; }
    .dr-slip .cell:first-child { border-left: 0; padding-left: 2px; }
    .eyebrow { font-size: .72rem; font-weight: 600; letter-spacing: .08em;
               text-transform: uppercase; color: var(--ftds-text-dim, #888); }
    .dr-slip .v { font-family: var(--ftds-font-mono, Consolas, monospace); font-variant-numeric: tabular-nums;
                  font-size: 26px; line-height: 1.15; color: var(--ftds-text, #fff); margin-top: 2px;
                  transition: color .15s ease; }
    .dr-slip .v small { font-size: 13px; color: var(--ftds-text-muted, #9a9ba0); margin-left: 2px;
                        font-family: var(--ftds-font, sans-serif); }
    .dr-slip .v.pending { color: var(--ftds-text-dim, #888); }
    .dr-slip .v.na { font-size: 15px; color: var(--ftds-text-dim, #888); padding-top: 6px; }
    .dr-slip .m { font-size: .78rem; color: #9b9b9b; white-space: nowrap;
                  overflow: hidden; text-overflow: ellipsis; }
    .dr-slip .m.lift { color: var(--ftds-warn, #f0b429); }

    .dr-hl { display: flex; flex-wrap: wrap; gap: 6px 8px; padding: 0 12px 10px; }
    .hl { display: inline-flex; align-items: center; gap: 7px; padding: 4px 10px 4px 8px;
          font-size: .78rem; color: var(--ftds-text-caption, #ccc);
          background: var(--ftds-surface-1, #131418);
          border: 1px solid var(--ftds-border-card, #2b2e34); border-radius: 999px; }
    .hl::before { content: ""; width: 7px; height: 7px; border-radius: 50%;
                  background: var(--ftds-text-dim, #888); flex: 0 0 auto; }
    .hl.ok::before { background: var(--ftds-success, #22c55e); }
    .hl.warn::before { background: var(--ftds-warn, #f0b429); }
    .hl.danger::before { background: var(--ftds-danger, #ef4444); }

    .dr-track { display: grid; grid-template-columns: 30px 1fr auto; align-items: center; gap: 10px;
                padding: 8px 12px 2px; border-top: 1px solid #252525; }
    .dr-play { width: 30px; height: 30px; padding: 0; display: grid; place-items: center;
               border: 1px solid var(--ftds-border-strong, #3a3d44); border-radius: 6px;
               background: var(--ftds-surface-1, #131418); color: var(--ftds-text-caption, #ccc); cursor: pointer; }
    .dr-play:hover { background: var(--ftds-surface-4, #24262b); border-color: #666; }
    .dr-play .pi { font-size: 11px; }
    .dr-lane { display: block; width: 100%; height: 72px; cursor: ew-resize; touch-action: none; }
    .dr-lane:focus-visible, .dr-play:focus-visible { outline: 2px solid var(--ftds-accent-strong, #d6321f); outline-offset: 2px; }
    .dr-time { font-family: var(--ftds-font-mono, Consolas, monospace); font-variant-numeric: tabular-nums;
               font-size: 12px; color: #9b9b9b; min-width: 74px; text-align: right; white-space: nowrap; }
    .dr-time b { color: #d5d5d5; font-weight: 500; }
    .pre-bg { fill: #101114; }
    .lane-bg { fill: #16171b; }
    .lane-edge { stroke: #474747; stroke-width: 1; }
    .mk { stroke: #474747; stroke-width: 1; stroke-dasharray: 2 3; }
    .mk.hit { stroke: #d5d5d5; stroke-dasharray: none; }
    .mk-t { font-size: 10px; fill: #9b9b9b; letter-spacing: .04em; }
    .mk-t.hit { fill: #d5d5d5; }
    .stage-t { font-size: 9px; font-weight: 500; fill: #9b9b9b; letter-spacing: .06em; text-transform: uppercase; }
    .stage-t.armed { fill: var(--ftds-accent-strong, #d6321f); }
    .start { stroke: #d5d5d5; stroke-width: 2; }
    .start.armed { stroke: var(--ftds-accent-strong, #d6321f); }
    .start.go { stroke: var(--ftds-success, #22c55e); }
    .car { fill: var(--ftds-accent-strong, #d6321f); }
    .car-g { font-family: var(--ftds-font-mono, Consolas, monospace); font-size: 10px; font-weight: 600; fill: #d5d5d5; }
    .evt { stroke: #2D9CDB; stroke-width: 1.5; }
    .evt.alert { stroke: #FF0000; }
    .ph-band { stroke: none; }

    .dr-phases { display: flex; flex-wrap: wrap; gap: 4px 12px; padding: 2px 12px 8px;
                 font-size: .7rem; color: var(--ftds-text-dim, #888); }
    .dr-phases span { display: inline-flex; align-items: center; gap: 5px; }
    .dr-phases i { width: 9px; height: 9px; flex: 0 0 auto; }

    .dr-ctx { display: grid; grid-template-columns: auto 1fr; gap: 4px 12px; align-items: baseline;
              padding: 8px 12px 10px; border-top: 1px solid #252525; font-size: .78rem; color: #9b9b9b; }
    .dr-ctx .vals { color: var(--ftds-text-caption, #ccc); font-size: 12.5px;
                    font-family: var(--ftds-font-mono, Consolas, monospace); font-variant-numeric: tabular-nums; }
    .dr-ctx .vals.na { color: #9b9b9b; font-family: var(--ftds-font, sans-serif); }

    .dr-gauges { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
                 padding: 10px 12px 12px; border-top: 1px solid #252525; }
    .gauge { --c: #d5d5d5; position: relative; padding: 10px 12px 8px;
             background: var(--ftds-surface-1, #131418);
             box-shadow: inset 0 0 0 1px var(--ftds-border-card, #2b2e34);
             clip-path: var(--ftds-chamfer-sm, none);
             display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .gauge.off { opacity: .55; }
    .gauge .top { display: flex; align-items: center; gap: 8px; min-width: 0; }
    .gauge .sw { width: 9px; height: 9px; border-radius: 2px; background: var(--c); flex: 0 0 auto; }
    .gauge .lb { font-size: .72rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase;
                 color: var(--ftds-text-muted, #9a9ba0); white-space: nowrap;
                 overflow: hidden; text-overflow: ellipsis; }
    .gauge .peak { margin-left: auto; font-size: 10.5px; color: var(--ftds-text-dim, #888);
                   white-space: nowrap; font-variant-numeric: tabular-nums; }
    .gauge .row { display: flex; align-items: baseline; gap: 6px; }
    .gauge .cv { font-size: 27px; line-height: 1.05; color: var(--ftds-text, #fff);
                 font-family: var(--ftds-font-mono, Consolas, monospace); font-variant-numeric: tabular-nums; }
    .gauge.off .cv { font-size: 15px; color: var(--ftds-text-dim, #888); padding: 6px 0 12px; }
    .gauge .u { font-size: 12px; color: var(--ftds-text-muted, #9a9ba0); }
    .gauge svg { width: 100%; height: 44px; display: block; margin-top: 2px; overflow: visible; }
    .gauge .ar { fill: var(--c); opacity: .12; }
    .gauge .ln { fill: none; stroke: var(--c); stroke-width: 1.6; vector-effect: non-scaling-stroke; }
    .gauge .t0 { stroke: var(--ftds-success, #22c55e); stroke-width: 1; stroke-dasharray: 2 3; vector-effect: non-scaling-stroke; }
    .gauge .cur { stroke: #d5d5d5; stroke-width: 1; vector-effect: non-scaling-stroke; }
    .gauge .pt { fill: var(--c); stroke: var(--ftds-surface-1, #131418); stroke-width: 2; }

    .dr-foot { padding: 7px 12px; border-top: 1px solid #303030; color: #9b9b9b;
               font-size: 11px; line-height: 1.55; }

    @media (prefers-reduced-motion: reduce) {
      .dr-slip .v { transition: none; }
    }

    @media (max-width: 640px) {
      .dr-slip { grid-template-columns: repeat(2, 1fr); gap: 12px 8px; }
      .dr-slip .cell:nth-child(3) { border-left: 0; padding-left: 2px; }
      .dr-slip .v { font-size: 22px; }
      .dr-gauges { grid-template-columns: repeat(2, 1fr); }
      .gauge .cv { font-size: 23px; }
      .dr-ctx { grid-template-columns: 1fr; gap: 2px; }
      .dr-head .meta { display: none; }
      .dr-track { grid-template-columns: 30px 1fr; }
      .dr-time { grid-column: 2; text-align: left; min-width: 0; }
    }
  `],
})
export class DragRunCardComponent implements OnChanges, OnDestroy {
  @Input() data!: DragRunArtifact;

  readonly LANE = LANE;
  readonly GAUGE_W = GAUGE_W;
  readonly GAUGE_H = GAUGE_H;

  // Fixo por corrida: montado em ngOnChanges, nunca recalculado por quadro.
  slipCells: DragRunSlipCell[] = [];
  lane: LaneView = { phases: [], marks: [], events: [] };
  phaseLegend: { color: string; label: string }[] = [];
  contextRows: { label: string; value: string; unavailable: boolean }[] = [];
  gauges: GaugeView[] = [];
  footerNote = '';

  // Estado do replay.
  frame!: Frame;
  playing = false;

  private tl!: Timeline;
  private armStart: number | null = null;
  private raf = 0;
  private lastTs = 0;
  private dragging = false;

  constructor(
    private transloco: TranslocoService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  t = (key: string, params?: Record<string, unknown>): string =>
    this.transloco.translate(key, params);

  ngOnChanges(): void {
    this.stop();
    this.tl = buildTimeline(this.data);
    this.armStart = twoStepArmStart(this.data, this.tl);

    this.slipCells = dragRunSlipCells(this.data, this.t);
    this.lane = this.buildLane();
    this.phaseLegend = this.buildPhaseLegend();
    this.contextRows = this.buildContextRows();
    this.gauges = this.buildGauges();
    this.footerNote = this.buildFooterNote();

    // Em repouso o card já mostra a resposta completa (Seção 10); o replay é opcional.
    this.render(this.tl.tEnd);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  get validityLabel(): string {
    return this.t(`chatbot.dragRun.validity.${this.data.run.validity}`);
  }

  /** Seção 5 obriga "estimadas · ±X s" no cabeçalho — e proíbe a palavra "oficial". */
  get headerMeta(): string {
    const r = this.data.run;
    return `${r.t0_label} · ${this.t('chatbot.dragRun.estimated')} · ±${secs(r.uncertainty_s ?? 0, 1)} s`;
  }

  get playLabel(): string {
    if (this.playing) return this.t('chatbot.dragRun.pause');
    return this.frame.atEnd ? this.t('chatbot.dragRun.restart') : this.t('chatbot.dragRun.play');
  }

  /** Célula ainda não alcançada pelo replay. Em repouso nada fica pendente. */
  isPending(c: DragRunSlipCell): boolean {
    return c.t !== undefined && !this.frame.atEnd && this.frame.t < c.t;
  }

  /* ───────────── replay ───────────── */

  togglePlay(): void {
    if (this.playing) { this.stop(); return; }
    if (this.frame.atEnd) this.render(this.tl.tStart);
    this.playing = true;
    this.lastTs = 0;
    this.zone.runOutsideAngular(() => { this.raf = requestAnimationFrame(this.tick); });
  }

  /** Roda fora da zona: um quadro por frame do navegador, em tempo real da corrida. */
  private tick = (ts: number): void => {
    if (!this.playing) return;
    const dt = this.lastTs ? (ts - this.lastTs) / 1000 : 0;
    this.lastTs = ts;
    const next = this.frame.t + dt;
    if (next >= this.tl.tEnd) {
      this.render(this.tl.tEnd);
      this.playing = false;
    } else {
      this.render(next);
      this.raf = requestAnimationFrame(this.tick);
    }
    this.cdr.detectChanges();
  };

  private stop(): void {
    this.playing = false;
    cancelAnimationFrame(this.raf);
  }

  onPointerDown(ev: PointerEvent, svg: Element): void {
    this.dragging = true;
    svg.setPointerCapture(ev.pointerId);
    this.stop();
    this.render(this.timeFromPointer(ev, svg));
  }

  onPointerMove(ev: PointerEvent, svg: Element): void {
    if (this.dragging) this.render(this.timeFromPointer(ev, svg));
  }

  onPointerUp(): void {
    this.dragging = false;
  }

  onKeydown(ev: KeyboardEvent): void {
    switch (ev.key) {
      case 'ArrowRight': this.stop(); this.render(this.frame.t + KEY_STEP_S); break;
      case 'ArrowLeft':  this.stop(); this.render(this.frame.t - KEY_STEP_S); break;
      case 'Home':       this.stop(); this.render(this.tl.tStart); break;
      case 'End':        this.stop(); this.render(this.tl.tEnd); break;
      case ' ':
      case 'Enter':      this.togglePlay(); break;
      default:           return;
    }
    ev.preventDefault();
  }

  private timeFromPointer(ev: PointerEvent, svg: Element): number {
    const r = svg.getBoundingClientRect();
    const x = r.width ? ((ev.clientX - r.left) / r.width) * LANE.w : LANE.x1;
    return timeAtLaneX(x, this.tl, LANE);
  }

  /** Posiciona tudo que se move no instante pedido. Não dispara detecção de mudanças. */
  private render(t: number): void {
    const tl = this.tl;
    const tCur = clamp(t, tl.tStart, tl.tEnd);
    const atEnd = tCur >= tl.tEnd - 1e-9;
    const i = indexAt(tl, tCur);
    const raw = i < 0 ? -1 : tl.idx[i];
    const hasTwoStep = this.data.two_step.available;

    // Carro: parado na linha até t0; depois posicionado pela distância percorrida.
    const carX = tCur < 0 ? LANE.x0 : laneXAtDistance(distanceAt(tl, tCur), tl, LANE);
    const gear = atEnd ? null : gearAt(this.data, tl, tCur, SHIFT_HOLD_S);

    // Linha de largada = luz de stage: vermelha armada, verde na soltura.
    const armed = this.armStart !== null && tCur >= this.armStart && tCur < 0;
    const go = hasTwoStep && tCur >= 0 && tCur < GO_HOLD_S;
    const stageLabel = hasTwoStep
      ? (armed ? `${this.t('chatbot.dragRun.stageTwoStep')} ${secs(-tCur, 1)}` : this.t('chatbot.dragRun.stageTwoStep'))
      : this.t('chatbot.dragRun.stageBefore');

    const hit = new Set<string>();
    for (const s of this.data.splits) {
      if (s.available && s.t_s !== undefined && tCur >= s.t_s - 1e-9) hit.add(s.id);
    }

    const gauges = this.gauges.map((g) => {
      if (!g.available) return { value: '', cx: 0, cy: null };
      const v = raw < 0 ? null : this.data.series[g.key][raw];
      return {
        value: v === null || v === undefined ? '—' : this.formatGauge(g, v),
        cx: g.xOf(tCur),
        cy: v === null || v === undefined ? null : g.yOf(v),
      };
    });

    const finish = this.data.splits.find((s) => s.id === '1/8');
    let timeStrong: string;
    let timeRest = '';
    if (atEnd && finish?.available && finish.t_s !== undefined) {
      timeStrong = `${secs(finish.t_s)} s`;
      timeRest = `${num(finish.kmh ?? 0)} km/h`;
    } else if (tCur < 0) {
      timeStrong = armed
        ? this.t('chatbot.dragRun.launchIn', { t: secs(-tCur, 1) })
        : `${signedSecs(tCur)} s`;
    } else {
      timeStrong = `${secs(tCur)} s`;
      timeRest = `${num(distanceAt(tl, tCur))} m`;
    }

    const span = tl.tEnd - tl.tStart || 1;
    this.frame = {
      t: tCur, atEnd, carX, carGear: gear ? `${gear}ª` : '', armed, go, stageLabel, hit, gauges,
      timeStrong, timeRest, ariaNow: Math.round(((tCur - tl.tStart) / span) * 100),
    };
  }

  /* ───────────── montagem por corrida ───────────── */

  private phaseColor(kind: string): string {
    const map: Record<string, string> = {
      idle: '#1e2024', burnout: '#6b2118', staging: '#6b5414', launch: '#1a6b3c',
      run: '#1a1c20', lift: '#2a4a6b', abort: '#6b1f1f',
    };
    return map[kind] ?? '#1e2024';
  }

  private buildLane(): LaneView {
    const tl = this.tl;

    const phases = this.data.phases
      .map((p) => {
        const x = Math.max(0, laneXAt(p.t_start_s, tl, LANE));
        const w = laneXAt(p.t_end_s, tl, LANE) - x;
        return {
          x, w, color: this.phaseColor(p.kind),
          title: `${this.t('chatbot.dragRun.phases.' + p.kind)} · ${secs(p.t_start_s)}s → ${secs(p.t_end_s)}s`,
        };
      })
      .filter((p) => p.w > 0.5);

    const marks = this.data.splits.map((s) => ({
      id: s.id,
      x: laneXAtDistance(s.m, tl, LANE),
      label: s.id === '1/8' ? `${this.t('chatbot.dragRun.finish')} · ${s.label}` : s.label,
      anchor: s.id === '1/8' ? 'end' : 'middle',
    }));

    const events = this.data.events
      .filter((e) => e.kind === 'gear' || e.kind === 'alert')
      .map((e) => ({
        x: laneXAt(e.t_s, tl, LANE),
        kind: e.kind,
        title: e.kind === 'gear'
          ? `${e.from}ª → ${e.to}ª · ${secs(e.t_s)}s`
          : `${e.name ?? this.t('chatbot.dragRun.unidentifiedAlert')} · ${secs(e.t_s)}s`,
      }));

    return { phases, marks, events };
  }

  private buildPhaseLegend() {
    const seen = [...new Set(this.data.phases.map((p) => p.kind))];
    return seen.map((k) => ({ color: this.phaseColor(k), label: this.t('chatbot.dragRun.phases.' + k) }));
  }

  private buildContextRows() {
    const snap = this.data.launch_snapshot;
    const rows: { label: string; value: string; unavailable: boolean }[] = [];

    // Campo ausente = canal não gravado. Nunca substituir por zero.
    const field = (key: keyof typeof snap, label: string, d: number, isSigned = false) =>
      snap[key] === undefined
        ? `${label} ${this.t('chatbot.dragRun.noChannel')}`
        : `${label} ${isSigned ? signed(snap[key] as number, d) : num(snap[key] as number, d)}`;

    rows.push({
      label: this.data.run.t0_rule === 'wot'
        ? this.t('chatbot.dragRun.atWot')
        : this.t('chatbot.dragRun.atRelease'),
      value: [
        snap.rpm === undefined ? `rpm ${this.t('chatbot.dragRun.noChannel')}` : `${num(snap.rpm)} rpm`,
        field('map_bar', 'boost', 2, true),
        field('oil_bar', this.t('chatbot.dragRun.oilShort'), 2),
        field('fuel_bar', this.t('chatbot.dragRun.fuelShort'), 2),
        field('battery_v', this.t('chatbot.dragRun.batteryShort'), 2),
        snap.gear === undefined ? '' : `${snap.gear}ª`,
      ].filter(Boolean).join(' · '),
      unavailable: false,
    });

    const ts = this.data.two_step;
    rows.push({
      label: this.t('chatbot.dragRun.twoStep'),
      value: ts.available
        ? this.t('chatbot.dragRun.twoStepDetail', {
            duration: secs(ts.duration_s ?? 0, 1),
            rpm: num(ts.rpm_hold ?? 0),
            battery: secs(ts.battery_min_v ?? 0),
          })
        : this.t(REASON_KEYS[ts.reason ?? ''] ?? 'chatbot.dragRun.unavailable'),
      unavailable: !ts.available,
    });

    if (this.data.flags.length) {
      rows.push({
        label: this.t('chatbot.dragRun.flags'),
        value: this.data.flags.map((f) => this.t('chatbot.dragRun.flag.' + f)).join(' · '),
        unavailable: true,
      });
    }
    return rows;
  }

  private formatGauge(g: GaugeSpec, v: number): string {
    return g.signed ? signed(v, g.digits) : num(v, g.digits);
  }

  /**
   * Os medidores compartilham a janela de tempo do replay (tStart → tEnd), para o cursor cair
   * no mesmo x em todos. Amostras depois de `end_s` ficam de fora: a corrida acabou ali.
   */
  private buildGauges(): GaugeView[] {
    const tl = this.tl;
    const inWindow = tl.t.map((t, i) => (t <= tl.tEnd ? i : -1)).filter((i) => i >= 0);
    const times = inWindow.map((i) => tl.t[i]);
    const xOf = (t: number) => ((t - tl.tStart) / (tl.tEnd - tl.tStart || 1)) * GAUGE_W;
    const t0x = xOf(0);

    return GAUGES.map((g) => {
      const values = inWindow.map((i) => this.data.series[g.key][tl.idx[i]]);
      const segments = dragRunSegments(times, values);
      const points = segments.flat();
      const label = this.t(g.labelKey);
      if (!points.length) {
        return { ...g, label, available: false, paths: [], areas: [], peak: '', t0x, xOf, yOf: () => 0 };
      }

      const vs = points.map((p) => p.v);
      const lo = Math.min(...vs), hi = Math.max(...vs);
      const pad = (hi - lo) * 0.12 || 1;
      const yOf = (v: number) => GAUGE_H - ((v - (lo - pad)) / ((hi + pad) - (lo - pad))) * GAUGE_H;

      // Um path por trecho: assim a lacuna aparece como lacuna, na linha e na área.
      const paths = segments.map((seg) =>
        seg.map((p, i) => `${i ? 'L' : 'M'}${xOf(p.t).toFixed(1)} ${yOf(p.v).toFixed(1)}`).join(' '));
      const areas = segments.map((seg) => {
        const first = xOf(seg[0].t).toFixed(1);
        const last = xOf(seg[seg.length - 1].t).toFixed(1);
        const line = seg.map((p) => `L${xOf(p.t).toFixed(1)} ${yOf(p.v).toFixed(1)}`).join(' ');
        return `M${first} ${GAUGE_H} ${line} L${last} ${GAUGE_H} Z`;
      });

      return {
        ...g, label, available: true, paths, areas, t0x, xOf, yOf,
        peak: this.t('chatbot.dragRun.peak', { max: this.formatGauge(g, hi), min: this.formatGauge(g, lo) }),
      };
    });
  }

  /** Seção 3: warnings unidos por " · ". `estimated_splits` está sempre entre eles. */
  private buildFooterNote(): string {
    const parts = this.data.warnings.map((w) => w.msg);
    if (this.data.downsampled_to) {
      // Item 1 dos ajustes de apresentação: nota genérica, sem número de pontos.
      parts.push(this.t('chatbot.chart.reducedNote'));
    }
    return parts.join(' · ');
  }
}
